import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Logo } from "assets/index";
import EyeHiddenIcon from "assets/svg/EyeHiddenIcon";
import EyeVisibleIcon from "assets/svg/EyeVisibleIcon";
import { AxiosError } from "axios";
import PrimaryButton from "components/PrimaryButton/PrimaryButton";
import * as motion from "motion/react-client";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ForgetPasswordRequest } from "reduxStore/dataTypes";
import {
  requestResetPasswordApi,
  requestUpdatePasswordApi,
} from "services/apiService";
import theme, { responsiveFontSize } from "themes/Theme";
import { commonStyle } from "../common.styles";

const ForgetPasswordPage = () => {
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const [isPasswordRepeatVisible, setIsPasswordRepeatVisible] = useState(false);
  const [email, setEmail] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [password, setPassword] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [confirmPassword, setConfirmPassword] = useState<string | null>(null);
  const [confirmPasswordError, setConfirmPasswordError] = useState<
    string | null
  >(null);
  const [otp, setOtp] = useState<string[]>(new Array(4).fill(""));
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const [otpError, setOtpError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const [passwordSteps, setPasswordSteps] = useState(0);
  const navigate = useNavigate();

  const handleTogglePasswordVisibility = () => {
    setIsPasswordVisible(!isPasswordVisible);
  };

  const handleTogglePasswordRepeatVisibility = () => {
    setIsPasswordRepeatVisible(!isPasswordRepeatVisible);
  };

  const handleChange = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return; // Allow only numbers

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Move to the next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleEmailValidation = async () => {
    try {
      if (email) {
        if (
          email.match(
            /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}(?:\.[a-zA-Z]+)*$/
          )
        ) {
          setLoading(true);
          const response = await requestResetPasswordApi(email);
          if (response && response.status === 200) {
            setPassword("");
            setConfirmPassword("");
            setPasswordSteps(1);
          }
          setLoading(false);
        } else {
          setEmailError("Please enter a valid email address");
        }
      } else {
        setEmailError("Email is required");
      }
    } catch (error) {
      setLoading(false);
      if (error instanceof AxiosError) {
        toast.error(error.response?.data?.message);
      }
    }
  };

  const handlePasswordUpdate = async () => {
    try {
      if (password === "" || password === null || password === undefined) {
        setPasswordError("Password is required");
      } else if (
        confirmPassword === "" ||
        confirmPassword === null ||
        confirmPassword === undefined
      ) {
        setConfirmPasswordError("Confirm Password is required");
      } else if (password !== confirmPassword) {
        setConfirmPasswordError("Password and Confirm Password do not match");
      } else if (
        otp.join("") === "" ||
        otp.join("") === null ||
        otp.join("") === undefined
      ) {
        setOtpError("OTP is required");
      } else if (otp.join("").length !== 4) {
        setOtpError("OTP must be 4 digits");
      } else {
        if (email) {
          setLoading(true);
          const body: ForgetPasswordRequest = {
            email: email,
            otp: otp.join(""),
            password: password,
          };
          const response = await requestUpdatePasswordApi(body);
          if (response && response.status === 200) {
            toast.success(response?.data?.message);
            navigate("/auth/login");
          }
          setLoading(false);
        }
      }
    } catch (error) {
      setLoading(false);
      if (error instanceof AxiosError) {
        toast.error(error.response?.data?.message);
      }
    }
  };

  return (
    <motion.div
      initial={{ x: "100%" }}
      animate={{ x: 0 }}
      transition={{
        type: "spring",
        stiffness: 50,
      }}
      style={{
        width: "80%",
      }}
    >
      <Box>
        <center>
          <Box
            component={"img"}
            src={Logo}
            sx={{
              width: "120px",
              height: "120px",
              objectFit: "contain",
              "&:hover": { cursor: "pointer" },
            }}
            onClick={() => {
              navigate("/");
            }}
          />
          <Typography style={commonStyle.titleText} mb={4}>
            Forget Password
          </Typography>
        </center>
      </Box>
      {passwordSteps === 0 ? (
        <>
          <Typography sx={commonStyle.inputLabelStyle}>
            Email address
          </Typography>
          <TextField
            placeholder="Enter email address"
            type="email"
            value={email}
            onChange={(e) => {
              if (e.target.value) {
                setEmailError(null);
              }
              setEmail(e.target.value);
            }}
            fullWidth
            slotProps={commonStyle.inputSlotTextStyle}
            error={!!emailError}
            helperText={emailError ? emailError : ""}
          />
        </>
      ) : passwordSteps === 1 ? (
        <>
          <Typography sx={commonStyle.inputLabelStyle}>New Password</Typography>

          <TextField
            type={isPasswordVisible ? "text" : "password"}
            margin="dense"
            fullWidth
            size="small"
            value={password}
            onChange={(e) => {
              if (e.target.value) {
                setPasswordError(null);
              }
              setPassword(e.target.value);
            }}
            slotProps={{
              input: {
                sx: commonStyle.inputSlotTextStyle,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleTogglePasswordVisibility}
                      aria-label="toggle password visibility"
                    >
                      {isPasswordVisible ? (
                        <EyeHiddenIcon />
                      ) : (
                        <EyeVisibleIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
            error={!!passwordError}
            helperText={passwordError ? passwordError : ""}
          />

          <Typography sx={commonStyle.inputLabelStyle}>
            Confirm Password
          </Typography>
          <TextField
            type={isPasswordRepeatVisible ? "text" : "password"}
            margin="dense"
            fullWidth
            size="small"
            value={confirmPassword}
            onChange={(e) => {
              if (e.target.value) {
                setConfirmPasswordError(null);
              }
              setConfirmPassword(e.target.value);
            }}
            slotProps={{
              input: {
                sx: commonStyle.inputSlotTextStyle,
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleTogglePasswordRepeatVisibility}
                      aria-label="toggle password visibility"
                    >
                      {isPasswordRepeatVisible ? (
                        <EyeHiddenIcon />
                      ) : (
                        <EyeVisibleIcon />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              },
            }}
            error={!!confirmPasswordError}
            helperText={confirmPasswordError ? confirmPasswordError : ""}
          />

          <Typography sx={commonStyle.inputLabelStyle}>
            Enter OTP received on your email
          </Typography>
          <Stack direction={"row"} alignItems={"center"} gap={2} mt={2}>
            {otp.map((digit, index) => (
              <TextField
                key={index}
                inputRef={(el) => (inputRefs.current[index] = el)}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                variant="outlined"
                size="small"
                slotProps={{
                  input: {
                    style: {
                      fontSize: "20px",
                      backgroundColor: "rgba(13, 104, 13, 0.62)",
                    },
                  },
                }}
                sx={{
                  width: "45px",
                  height: "45px",
                  "& input": {
                    textAlign: "center", // Centering the text inside the input
                  },
                }}
              />
            ))}
          </Stack>
          <Typography sx={styles.errorText}>{otpError}</Typography>
        </>
      ) : null}
      {passwordSteps === 0 ? (
        loading ? (
          <CircularProgress sx={{ mt: 6 }} />
        ) : (
          <PrimaryButton
            title="Next"
            onClick={handleEmailValidation}
            viewStyle={{ width: "100%", mt: 6 }}
          />
        )
      ) : loading ? (
        <CircularProgress sx={{ mt: 6 }} />
      ) : (
        <PrimaryButton
          title="Submit"
          onClick={() => {
            handlePasswordUpdate();
          }}
          viewStyle={{ width: "100%", mt: 6 }}
        />
      )}

      <Divider
        flexItem
        sx={{ borderColor: "rgba(245, 245, 245, 1)", mt: 4, pb: 4 }}
      >
        Or
      </Divider>
      <center>
        <Button
          variant="text"
          onClick={() => {
            navigate(-1);
          }}
        >
          <Typography sx={{ ...styles.goBackBtn, mt: 2 }}>Go Back</Typography>
        </Button>
      </center>
    </motion.div>
  );
};
export default ForgetPasswordPage;

const styles = {
  goBackBtn: {
    fontSize: responsiveFontSize(16),
    color: theme.palette.primary.main,
    textTransform: "capitalize",
    "&:hover": {
      cursor: "pointer",
    },
  },
  errorText: {
    fontSize: responsiveFontSize(12),
    fontWidth: 400,
    color: "red",
    marginBottom: "3em",
  },
};
